import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, Search, ChevronLeft, ChevronRight, Loader2, 
  Ban, Trash2, Eye, Shield, AlertTriangle, Check, X, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../../components/ui/dialog";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export default function AdminUsers() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [banData, setBanData] = useState({ ban_type: "temporary", duration_days: 7, reason: "" });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({ page, limit: 20, search: search || undefined, status: statusFilter || undefined });
      setUsers(response.data.users);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (error) {
      toast.error("Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleBan = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await adminApi.banUser(selectedUser.user_id, banData);
      toast.success("Kullanıcı banlandı");
      setShowBanDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnban = async (userId) => {
    try {
      await adminApi.unbanUser(userId);
      toast.success("Ban kaldırıldı");
      fetchUsers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await adminApi.deleteUser(selectedUser.user_id);
      toast.success("Kullanıcı silindi");
      setShowDeleteDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setActionLoading(false);
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
          <p className="text-[#B1C1C1]">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-users">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
          <Home className="w-5 h-5" />
        </Link>
        <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#4DFFFF]" />
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Kullanıcı Yönetimi
          </h1>
        </div>
        <span className="ml-auto text-sm text-[#64748B]">{total} kullanıcı</span>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="İsim, kullanıcı adı veya e-posta ara..."
                className="pl-9 bg-[#050E2E]/50 border-white/10 text-white"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white"
          >
            <option value="">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="banned">Banlı</option>
          </select>
          <Button type="submit" className="bg-[#4DFFFF] text-[#050E2E]">Ara</Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0A1635]">
                <tr>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Kullanıcı</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">E-posta</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Kayıt</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Son Giriş</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">Durum</th>
                  <th className="text-left p-4 text-xs text-[#64748B] font-medium uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-sm font-bold text-[#C6A87C]">{u.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-xs text-[#64748B]">@{u.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-[#B1C1C1]">{u.email}</td>
                    <td className="p-4 text-sm text-[#64748B]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "-"}
                    </td>
                    <td className="p-4 text-sm text-[#64748B]">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString("tr-TR") : "-"}
                    </td>
                    <td className="p-4">
                      {u.status === "banned" ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">Banlı</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Aktif</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/profile/${u.username}`}
                          className="p-2 rounded-lg hover:bg-white/10 text-[#64748B] hover:text-white"
                          title="Profili Gör"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        {u.email !== ADMIN_EMAIL && (
                          <>
                            {u.status === "banned" ? (
                              <button
                                onClick={() => handleUnban(u.user_id)}
                                className="p-2 rounded-lg hover:bg-green-500/20 text-green-400"
                                title="Banı Kaldır"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => { setSelectedUser(u); setShowBanDialog(true); }}
                                className="p-2 rounded-lg hover:bg-yellow-500/20 text-yellow-400"
                                title="Banla"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => { setSelectedUser(u); setShowDeleteDialog(true); }}
                              className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-white/5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#B1C1C1]">{page} / {pages}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Ban Dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Kullanıcıyı Banla</DialogTitle>
            <DialogDescription className="text-[#B1C1C1]">
              {selectedUser?.name} (@{selectedUser?.username}) kullanıcısını banlamak üzeresiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#B1C1C1]">Ban Türü</label>
              <select
                value={banData.ban_type}
                onChange={(e) => setBanData({ ...banData, ban_type: e.target.value })}
                className="w-full mt-1 px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white"
              >
                <option value="temporary">Geçici Ban</option>
                <option value="permanent">Kalıcı Ban</option>
              </select>
            </div>
            {banData.ban_type === "temporary" && (
              <div>
                <label className="text-sm text-[#B1C1C1]">Süre (Gün)</label>
                <Input
                  type="number"
                  min="1"
                  value={banData.duration_days}
                  onChange={(e) => setBanData({ ...banData, duration_days: parseInt(e.target.value) })}
                  className="mt-1 bg-[#050E2E]/50 border-white/10 text-white"
                />
              </div>
            )}
            <div>
              <label className="text-sm text-[#B1C1C1]">Sebep</label>
              <Input
                value={banData.reason}
                onChange={(e) => setBanData({ ...banData, reason: e.target.value })}
                placeholder="Ban sebebi (opsiyonel)"
                className="mt-1 bg-[#050E2E]/50 border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBanDialog(false)}>İptal</Button>
            <Button onClick={handleBan} disabled={actionLoading} className="bg-yellow-500 text-black">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Banla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Kullanıcıyı Sil
            </DialogTitle>
            <DialogDescription className="text-[#B1C1C1]">
              {selectedUser?.name} (@{selectedUser?.username}) kullanıcısını kalıcı olarak silmek üzeresiniz. Bu işlem geri alınamaz!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>İptal</Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-red-500 text-white">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
